import { AlertTriangle } from 'lucide-react';
import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import { ROUTE_PATHS } from '../../app/router/route-paths';
import { fetchOrgPlanStatus } from '../../features/tariffs/api/org-tariff.api';
import {
  ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_LIMIT_GRACE,
  ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_NEEDS_PAYMENT,
  ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_ORG_GRACE,
  ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_POST_GRACE,
  ORG_LIFECYCLE_BANNER_NOT_A_MODULE_SETTING,
  ORG_LIFECYCLE_PRIMARY_CTA_LABEL,
  ORG_LIFECYCLE_QUOTA_LIMIT_BANNER_TITLE,
  ORG_LIFECYCLE_READ_ONLY_STRIP_FALLBACK,
  ORG_LIFECYCLE_SUMMARY_NEEDS_PAYMENT,
  ORG_LIFECYCLE_SUMMARY_POST_GRACE,
  orgLifecycleQuotaLimitBannerBody,
} from '../../features/tariffs/lib/org-lifecycle-copy';
import { buildLifecycleSummaryLine, formatOrgPlanGraceDateRu } from '../../features/tariffs/lib/org-tariff-page-view-model';
import type { OrgPlanStatus } from '../../features/tariffs/lib/tariff-org-types';
import { isStaffPlatformMode } from '../../shared/access/staff-platform-mode';
import { useAuth } from '../../shared/auth/use-auth';
import { userFacingLoadError } from '../../shared/lib/staff-user-facing-error';
import { getButtonClassNames } from '../../shared/ui/Button';

const REGION_LABEL =
  'Срок подписки и тариф организации: баннер и при необходимости полоса read-only' as const;

type PrimaryKind = 'post_grace' | 'grace_dated' | 'needs_payment';

function resolvePrimarySubscriptionBanner(status: OrgPlanStatus): PrimaryKind | null {
  if (status.post_grace_unpaid === true) return 'post_grace';
  if (status.in_org_tariff_grace === true && (status.org_tariff_grace_ends_at ?? '').trim().length > 0) {
    return 'grace_dated';
  }
  if (status.needs_payment_banner === true) return 'needs_payment';
  return null;
}

function quotaNearExtraNear(status: OrgPlanStatus): string {
  const parts: string[] = [];
  if (status.near_limit_employees) parts.push('сотрудники');
  if (status.near_limit_clients) parts.push('клиенты');
  if (status.near_limit_branches) parts.push('филиалы');
  if (parts.length === 0) return '';
  return ` Дополнительно: использовано не менее 95% лимита по: ${parts.join(', ')}.`;
}

function shouldLoadPlanStatus(me: ReturnType<typeof useAuth>['me'], impersonateOrgId: number | null): boolean {
  if (!me) return false;
  return !isStaffPlatformMode(me, impersonateOrgId);
}

/**
 * Жёлтый баннер по GET /organizations/me/plan-status и полоса read-only по `org_tariff_write` (ADR-0019).
 * Между topbar и контентом (`AppShell`).
 */
export function StaffOrgTariffLifecycleShellBanner() {
  const { apiClient, me, impersonateOrgId } = useAuth();
  const [status, setStatus] = useState<OrgPlanStatus | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);

  const reload = useCallback(async () => {
    if (!shouldLoadPlanStatus(me, impersonateOrgId)) {
      setStatus(null);
      setLoadError(null);
      return;
    }
    try {
      const s = await fetchOrgPlanStatus(apiClient);
      setStatus(s);
      setLoadError(null);
    } catch (e) {
      setStatus(null);
      setLoadError(userFacingLoadError(e));
    }
  }, [apiClient, me, impersonateOrgId]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const orgTariffWrite = me?.capabilities?.org_tariff_write;

  const primaryKind = useMemo(() => (status ? resolvePrimarySubscriptionBanner(status) : null), [status]);

  const primarySubscriptionBannerVisible = primaryKind != null;

  const showQuotaBanner = useMemo(() => {
    if (!status?.has_plan) return false;
    const until = (status.limit_grace_until ?? '').trim();
    return status.in_tariff_limit_grace === true && until.length > 0;
  }, [status]);

  /** Как в legacy e2e: полоса read-only отдельно от жёлтого «срок подписки», если верхний баннер по подписке не показан. */
  const showReadOnlyStrip =
    orgTariffWrite != null &&
    orgTariffWrite.allowed === false &&
    !primarySubscriptionBannerVisible;

  const stripText = (orgTariffWrite?.detail ?? '').trim() || ORG_LIFECYCLE_READ_ONLY_STRIP_FALLBACK;

  const quotaBody = useMemo(() => {
    if (!status || !showQuotaBanner) return null;
    const graceFormatted = formatOrgPlanGraceDateRu(status.limit_grace_until);
    return orgLifecycleQuotaLimitBannerBody(graceFormatted, quotaNearExtraNear(status));
  }, [showQuotaBanner, status]);

  const primaryBlocks = useMemo(() => {
    if (!status || !primaryKind) return null;
    if (primaryKind === 'post_grace') {
      return (
        <div className="border-b border-amber-500/40 bg-amber-500/[0.12] px-4 py-3 dark:border-amber-500/35 dark:bg-amber-950/40">
          <div className="mx-auto flex w-full max-w-none items-start gap-3 px-3 sm:px-5">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden />
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm font-semibold text-[hsl(var(--ui-foreground))]">Срок льготы по тарифу истёк</p>
              <p className="text-sm leading-snug text-[hsl(var(--ui-foreground))]">{ORG_LIFECYCLE_SUMMARY_POST_GRACE}</p>
              <p className="text-xs leading-snug text-[hsl(var(--ui-muted-foreground))]">
                {ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_POST_GRACE}
              </p>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Link
                  to={ROUTE_PATHS.tariffs}
                  className={getButtonClassNames({ variant: 'secondary', size: 'sm', className: 'inline-flex' })}
                >
                  {ORG_LIFECYCLE_PRIMARY_CTA_LABEL}
                </Link>
                <span className="text-xs text-[hsl(var(--ui-muted-foreground))]">{ORG_LIFECYCLE_BANNER_NOT_A_MODULE_SETTING}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    if (primaryKind === 'grace_dated') {
      const line = buildLifecycleSummaryLine(status);
      return (
        <div className="border-b border-amber-500/40 bg-amber-500/[0.12] px-4 py-3 dark:border-amber-500/35 dark:bg-amber-950/40">
          <div className="mx-auto flex w-full max-w-none items-start gap-3 px-3 sm:px-5">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden />
            <div className="min-w-0 flex-1 space-y-2">
              <p className="text-sm font-semibold text-[hsl(var(--ui-foreground))]">Льготный период после срока оплаты</p>
              {line ? (
                <p className="text-sm leading-snug text-[hsl(var(--ui-foreground))]">{line}</p>
              ) : null}
              <p className="text-xs leading-snug text-[hsl(var(--ui-muted-foreground))]">
                {ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_ORG_GRACE}
              </p>
              <div className="flex flex-wrap items-center gap-2 pt-1">
                <Link
                  to={ROUTE_PATHS.tariffs}
                  className={getButtonClassNames({ variant: 'secondary', size: 'sm', className: 'inline-flex' })}
                >
                  {ORG_LIFECYCLE_PRIMARY_CTA_LABEL}
                </Link>
                <span className="text-xs text-[hsl(var(--ui-muted-foreground))]">{ORG_LIFECYCLE_BANNER_NOT_A_MODULE_SETTING}</span>
              </div>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div className="border-b border-amber-500/40 bg-amber-500/[0.12] px-4 py-3 dark:border-amber-500/35 dark:bg-amber-950/40">
        <div className="mx-auto flex w-full max-w-none items-start gap-3 px-3 sm:px-5">
          <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0 text-amber-700 dark:text-amber-300" aria-hidden />
          <div className="min-w-0 flex-1 space-y-2">
            <p className="text-sm font-semibold text-[hsl(var(--ui-foreground))]">Требуется оплата или тариф</p>
            <p className="text-sm leading-snug text-[hsl(var(--ui-foreground))]">{ORG_LIFECYCLE_SUMMARY_NEEDS_PAYMENT}</p>
            <p className="text-xs leading-snug text-[hsl(var(--ui-muted-foreground))]">
              {ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_NEEDS_PAYMENT}
            </p>
            <div className="flex flex-wrap items-center gap-2 pt-1">
              <Link
                to={ROUTE_PATHS.tariffs}
                className={getButtonClassNames({ variant: 'secondary', size: 'sm', className: 'inline-flex' })}
              >
                {ORG_LIFECYCLE_PRIMARY_CTA_LABEL}
              </Link>
              <span className="text-xs text-[hsl(var(--ui-muted-foreground))]">{ORG_LIFECYCLE_BANNER_NOT_A_MODULE_SETTING}</span>
            </div>
          </div>
        </div>
      </div>
    );
  }, [primaryKind, status]);

  if (!shouldLoadPlanStatus(me, impersonateOrgId)) {
    return null;
  }

  if (!primarySubscriptionBannerVisible && !showQuotaBanner && !showReadOnlyStrip && !loadError) {
    return null;
  }

  return (
    <section aria-label={REGION_LABEL} className="w-full shrink-0 border-b border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-background))]">
      {loadError ? (
        <div className="border-b border-amber-600/30 bg-amber-500/[0.08] px-4 py-2 text-sm text-amber-900 dark:text-amber-100">
          Не удалось загрузить тариф: {loadError}
        </div>
      ) : null}
      {primaryBlocks}
      {showQuotaBanner && quotaBody ? (
        <div
          data-testid="org-quota-limit-grace-banner"
          className="border-b border-amber-500/40 bg-amber-500/[0.08] px-4 py-3 dark:border-amber-500/35 dark:bg-amber-950/35"
        >
          <div className="mx-auto flex w-full max-w-none flex-col gap-2 px-3 sm:px-5">
            <p className="text-sm font-semibold text-[hsl(var(--ui-foreground))]">{ORG_LIFECYCLE_QUOTA_LIMIT_BANNER_TITLE}</p>
            <p className="text-sm leading-snug text-[hsl(var(--ui-foreground))]">{quotaBody}</p>
            <p className="text-xs leading-snug text-[hsl(var(--ui-muted-foreground))]">
              {ORG_LIFECYCLE_BANNER_RECOVERY_FOOTER_LIMIT_GRACE}
            </p>
            <Link
              to={ROUTE_PATHS.tariffs}
              className={getButtonClassNames({
                variant: 'secondary',
                size: 'sm',
                className: 'inline-flex w-fit',
              })}
            >
              {ORG_LIFECYCLE_PRIMARY_CTA_LABEL}
            </Link>
          </div>
        </div>
      ) : null}
      {showReadOnlyStrip ? (
        <div
          data-testid="org-tariff-write-blocked"
          className="border-b border-[hsl(var(--ui-border))] bg-[hsl(var(--ui-muted))]/45 px-4 py-2 text-sm leading-snug text-[hsl(var(--ui-foreground))]"
          role="status"
        >
          <div className="mx-auto max-w-none px-3 sm:px-5">{stripText}</div>
        </div>
      ) : null}
    </section>
  );
}
